import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { usePricingSettings } from '../../hooks/usePricingSettings';
import { fullAddress, placeName, toDate, timeLabel, dayLabel } from '../../utils/orderCardFormat';
import { orderTitle } from '../../utils/orderTitle';
import { maxBid } from '../../utils/orderPricing';
import { loadingEquipmentText } from '../../utils/loadingEquipment';
import { orderTruckText, orderLoadsText, orderLoadSplitText, orderCraneLiftText } from '../../utils/orderTruck';
import { massAccountLines } from '../../utils/massAccount';
import { orderHourlyText } from '../../utils/orderHourly';
import { formatKr } from '../../utils/formatKr';
import { validateBid, submitBid, withdrawBid, bidErrorMessage, suggestedBidAmount } from '../../utils/bidSubmit';
import { takeOrder } from '../../utils/takeOrder';
import { useOrderActionState } from '../../hooks/useOrderActionState';
import InvoiceProfileHint from './InvoiceProfileHint';
import ConfirmDialog from './ConfirmDialog';
import OrderInlineDetails from './OrderInlineDetails';
import { OrdererChip, RouteLines } from './OrderSidebar';
import { OrderSupplierLogo } from '../common/SupplierLogo';
import OrdererPhotos from '../common/OrdererPhotos';
import { COLORS, FONTS } from '../../styles/tokens';

const stop = e => e.stopPropagation();
const BID_STEP = 50;

// Det åpnede oppdraget i lista (mobil og desktop): oppdragsgiver, adresser, bud direkte i kortet
// (eller «Godta fastpris») med bekreftelse før noe sendes, og «Se alle detaljer» som folder ut mer info
// inni samme kort. Sperrene (manglende profil, forfalt gebyr) er de samme som ellers.
export default function ExpandedOrder({ order, existingBid, onTaken }) {
  const { currentUser, userDoc } = useAuth();
  const { take, bid, blocked } = useOrderActionState(order, existingBid);
  const { baseRate } = usePricingSettings();

  const cap = maxBid(order);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [amount, setAmount] = useState(String(existingBid?.amount ?? cap ?? ''));
  const [message, setMessage] = useState('');
  const [msgOpen, setMsgOpen] = useState(false);
  const [fieldError, setFieldError] = useState('');
  const [editing, setEditing] = useState(false);
  const [fixedBidOpen, setFixedBidOpen] = useState(false);
  const [confirm, setConfirm] = useState(null); // { type: 'bid' | 'take' | 'withdraw', amount? }
  const [busy, setBusy] = useState(false);
  const [dialogError, setDialogError] = useState('');
  const [canRetry, setCanRetry] = useState(true);
  const [sentInfo, setSentInfo] = useState(null); // { amount, kind: 'sent' | 'updated' }
  const [withdrawnId, setWithdrawnId] = useState(null);
  const [pendingMessage, setPendingMessage] = useState('');
  const busyRef = useRef(false);

  // Budet kan komme inn (eller endres) fra sanntidslytteren etter at vi har sendt
  const liveBid = existingBid && existingBid.bidId !== withdrawnId ? existingBid : null;
  // Det vi nettopp sendte gjelder til sanntidslytteren har tatt det igjen
  const bidAmount = sentInfo ? sentInfo.amount : (liveBid?.amount ?? null);
  const hasBid = bidAmount != null;
  useEffect(() => { if (liveBid) setAmount(String(liveBid.amount)); }, [liveBid?.amount]); // eslint-disable-line react-hooks/exhaustive-deps

  const suggested = suggestedBidAmount(order, baseRate);
  const route = `${placeName(order.pickupAddress)} → ${placeName(order.deliveryAddress)}`;
  const title = orderTitle(order);
  const scheduled = toDate(order.scheduledAt);
  const meta = [
    scheduled && `Levering ${dayLabel(scheduled).toLowerCase()} ${timeLabel(scheduled)}`,
    orderHourlyText(order),
    loadingEquipmentText(order),
    [orderTruckText(order), orderLoadsText(order), orderLoadSplitText(order), orderCraneLiftText(order)].filter(Boolean).join(', ') || null,
  ].filter(Boolean);
  const accountLines = massAccountLines(order);

  const showTake = take && !hasBid && !pendingMessage;
  const showForm = bid && (!hasBid || editing) && (!showTake || fixedBidOpen);
  const lock = blocked ? styles.locked : null;

  function closeDialog() {
    if (busyRef.current) return;
    setConfirm(null);
    setDialogError('');
    setCanRetry(true);
  }

  function onSubmit(e) {
    e.preventDefault();
    if (blocked || busyRef.current) return;
    const err = validateBid(amount, order, liveBid);
    if (err) { setFieldError(err); return; }
    setFieldError('');
    setDialogError('');
    setConfirm({ type: 'bid', amount: Number(amount) });
  }

  function onAmountChange(e) {
    setAmount(e.target.value.replace(/\D/g, ''));
    if (fieldError) setFieldError('');
  }

  function stepAmount(delta) {
    const base = Number(amount) || Number(suggested ?? cap ?? 0);
    let next = Math.max(0, base + delta);
    if (cap != null && next > cap) {
      // Over taket: stopp på taket og si fra at man ikke kan by høyere
      setFieldError(`Du kan ikke by høyere enn ${formatKr(cap)}.`);
      next = cap;
    } else if (fieldError) setFieldError('');
    setAmount(String(next));
  }

  // Tastaturet dekker gjerne inputfeltet: rull det til midten etter at det har kommet opp
  function onAmountFocus(e) {
    const el = e.target;
    setTimeout(() => el.scrollIntoView?.({ block: 'center', behavior: 'smooth' }), 300);
  }

  async function runBusy(fn) {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    setDialogError('');
    try { await fn(); } finally { busyRef.current = false; setBusy(false); }
  }

  const confirmBid = () => runBusy(async () => {
    try {
      const kind = await submitBid({
        order, existingBid: liveBid, amount: confirm.amount, message, uid: currentUser?.uid, userDoc,
      });
      setSentInfo({ amount: confirm.amount, kind });
      setWithdrawnId(null);
      setEditing(false);
      setMessage('');
      setMsgOpen(false);
      setConfirm(null);
    } catch (err) {
      setDialogError(bidErrorMessage(err));
    }
  });

  const confirmWithdraw = () => runBusy(async () => {
    try {
      await withdrawBid(liveBid?.bidId);
      setWithdrawnId(liveBid.bidId);
      setSentInfo(null);
      setEditing(false);
      setAmount(String(cap ?? ''));
      setConfirm(null);
    } catch (err) {
      setDialogError(bidErrorMessage(err));
    }
  });

  const confirmTake = () => runBusy(async () => {
    const res = await takeOrder(order.id);
    if (res.status === 'matched') {
      setConfirm(null);
      onTaken?.(order);
    } else if (res.status === 'pending') {
      setPendingMessage(res.message);
      setConfirm(null);
    } else {
      setCanRetry(res.canRetry);
      setDialogError(res.message);
    }
  });

  const kind = sentInfo && sentInfo.amount === bidAmount && sentInfo.kind === 'updated' ? 'Bud oppdatert' : 'Bud sendt';

  return (
    <div style={styles.wrap} data-testid="order-peek" onClick={stop}>
      <OrdererChip ordererId={order.ordererUid} variant="row" />
      <div style={{ marginTop: 12 }}>
        <RouteLines pickupLogo={<OrderSupplierLogo order={order} />} pickup={fullAddress(order.pickupAddress)} delivery={fullAddress(order.deliveryAddress)} />
      </div>
      {meta.length > 0 && <div style={styles.meta}>{meta.join(' · ')}</div>}
      {accountLines.map(l => (
        <div key={l.label} style={l.label === 'Merk med' ? styles.markWith : styles.meta}>{l.label}: {l.value}</div>
      ))}
      <OrdererPhotos order={order} />
      <InvoiceProfileHint order={order} />

      <button type="button" style={{ ...styles.linkBtn, marginTop: 4 }} aria-expanded={detailsOpen} onClick={() => setDetailsOpen(o => !o)}>
        {detailsOpen ? 'Skjul detaljer' : 'Se alle detaljer'}
      </button>
      {detailsOpen && <OrderInlineDetails order={order} />}

      {hasBid && !editing && (
        <div role="status" style={styles.bidLine}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={COLORS.successText} strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ flexShrink: 0 }}>
            <path d="M5 12.5l4.5 4.5L19 7.5" />
          </svg>
          <span style={{ flex: 1, minWidth: 0 }}>
            <b>{kind} · {formatKr(bidAmount)}</b>
            <span style={styles.bidSub}>Venter på bestiller</span>
          </span>
        </div>
      )}
      {hasBid && !editing && bid && (
        <div style={styles.bidActions}>
          <button type="button" className="tippi-btn-secondary" disabled={blocked} style={{ ...styles.outlineBtn, ...lock }}
                  onClick={() => { setAmount(String(bidAmount)); setEditing(true); }}>
            Endre bud
          </button>
          {liveBid?.bidId && (
            <button type="button" style={styles.linkBtn} onClick={() => { setDialogError(''); setConfirm({ type: 'withdraw' }); }}>
              Trekk bud
            </button>
          )}
        </div>
      )}

      {pendingMessage && (
        <div role="status" style={styles.bidLine}>
          <span style={{ flex: 1, minWidth: 0 }}>
            <b>Sendt til bestiller for godkjenning</b>
            <span style={styles.bidSub}>{pendingMessage}</span>
          </span>
        </div>
      )}

      {showTake && (
        <button type="button" className="tippi-btn" disabled={blocked} style={{ ...styles.primaryBtn, ...lock }}
                onClick={() => { setDialogError(''); setCanRetry(true); setConfirm({ type: 'take' }); }}>
          Godta fastpris
        </button>
      )}

      {showTake && bid && (
        <button type="button" style={styles.linkBtn} aria-expanded={fixedBidOpen} onClick={() => setFixedBidOpen(o => !o)}>
          Eller by under fastprisen
        </button>
      )}

      {showForm && (
        <form
          noValidate onSubmit={onSubmit} style={styles.form}
          onClick={stop} onTouchStart={stop} onTouchMove={stop} onTouchEnd={stop} onMouseDown={stop}
        >
          <div>
            <div style={styles.stepRow}>
              <button type="button" aria-label="Senk bud med 50 kroner" style={styles.stepBtn}
                      disabled={blocked || !(Number(amount) > 0)} onClick={() => stepAmount(-BID_STEP)}>−</button>
            <div style={{ ...styles.inputWrap, ...(fieldError ? styles.inputWrapError : {}) }}>
              <span style={styles.prefix} aria-hidden="true">kr</span>
              <input
                style={styles.input}
                type="text"
                inputMode="numeric"
                autoComplete="off"
                aria-label="Beløp i kroner"
                aria-invalid={fieldError ? 'true' : undefined}
                aria-describedby={fieldError ? 'bid-error' : undefined}
                placeholder={String(suggested ?? cap ?? '0')}
                value={amount}
                disabled={blocked}
                onChange={onAmountChange}
                onFocus={onAmountFocus}
              />
            </div>
              <button type="button" aria-label="Øk bud med 50 kroner" style={styles.stepBtn}
                      disabled={blocked} onClick={() => stepAmount(BID_STEP)}>+</button>
            </div>
          </div>
          {fieldError && <p id="bid-error" role="alert" style={styles.error}>{fieldError}</p>}

          {msgOpen ? (
            <textarea
              style={styles.message}
              placeholder="Melding til bestiller (valgfri)"
              aria-label="Melding til bestiller"
              value={message}
              onChange={e => setMessage(e.target.value)}
              onFocus={onAmountFocus}
              maxLength={300}
              rows={2}
              disabled={blocked}
            />
          ) : (
            <button type="button" style={styles.linkBtn} disabled={blocked} onClick={() => setMsgOpen(true)}>
              Legg til melding
            </button>
          )}

          <button type="submit" className="tippi-btn" disabled={blocked} style={{ ...styles.primaryBtn, ...lock }}>
            {hasBid ? 'Endre bud' : 'Angi bud'}
          </button>
          {editing && (
            <button type="button" style={styles.linkBtn} onClick={() => { setEditing(false); setFieldError(''); }}>
              Avbryt endring
            </button>
          )}
        </form>
      )}

      {confirm?.type === 'bid' && (
        <ConfirmDialog
          title="Bekreft bud" confirmLabel="Send bud" busy={busy} error={dialogError}
          onConfirm={confirmBid} onCancel={closeDialog}
        >
          <div>{`Du byr ${formatKr(confirm.amount)} på ${title}, ${route}.`}</div>
          {hasBid && <div style={{ marginTop: 6 }}>{`Ditt forrige bud var ${formatKr(bidAmount)}.`}</div>}
        </ConfirmDialog>
      )}
      {confirm?.type === 'take' && (
        <ConfirmDialog
          title="Bekreft fastpris"
          confirmLabel={dialogError && canRetry ? 'Prøv igjen' : 'Godta fastpris'} busyLabel="Tar oppdraget…"
          cancelLabel={dialogError && !canRetry ? 'Lukk' : 'Avbryt'} hideConfirm={!!dialogError && !canRetry}
          busy={busy} error={dialogError} onConfirm={confirmTake} onCancel={closeDialog}
        >
          <div>{`Godta fastpris ${formatKr(order.fixedPrice)} på ${title}, ${route}?`}</div>
          <div style={{ marginTop: 6 }}>
            {`Du forplikter deg til å hente lasten${scheduled ? ` ${dayLabel(scheduled).toLowerCase()} kl. ${timeLabel(scheduled)}` : ''}. Oppdraget blir ditt med en gang.`}
          </div>
        </ConfirmDialog>
      )}
      {confirm?.type === 'withdraw' && (
        <ConfirmDialog
          title="Trekk bud?" confirmLabel="Trekk bud" busyLabel="Trekker…" danger
          busy={busy} error={dialogError} onConfirm={confirmWithdraw} onCancel={closeDialog}
        >
          {`Vil du trekke budet ditt på ${formatKr(bidAmount)} på ${title}, ${route}?`}
        </ConfirmDialog>
      )}
    </div>
  );
}

const styles = {
  wrap:    { borderTop: `1px solid ${COLORS.border}`, marginTop: 12, paddingTop: 12 },
  meta:    { fontSize: 13, color: COLORS.textMid, lineHeight: 1.4, marginBottom: 2 },
  markWith: { fontSize: 14, fontWeight: 700, color: COLORS.textDark, lineHeight: 1.4, marginBottom: 2 },
  locked:  { opacity: 0.5, cursor: 'not-allowed' },
  form:    { marginTop: 4 },
  stepRow: { display: 'flex', alignItems: 'center', gap: 8 },
  stepBtn: { flexShrink: 0, width: 46, height: 46, borderRadius: '50%', border: `1.5px solid ${COLORS.border}`, background: COLORS.white, color: COLORS.textDark, fontSize: 24, fontWeight: 700, lineHeight: 1, cursor: 'pointer' },
  inputWrap:      { flex: 1, minWidth: 0, display: 'flex', alignItems: 'baseline', gap: 8, background: COLORS.white, borderWidth: 1.5, borderStyle: 'solid', borderColor: COLORS.border, borderRadius: 14, padding: '6px 16px' },
  inputWrapError: { borderColor: COLORS.danger },
  prefix:  { fontFamily: FONTS.display, fontSize: 24, fontWeight: 700, color: COLORS.textLight },
  input:   { fontFamily: FONTS.display, fontSize: 36, fontWeight: 700, lineHeight: 1.2, color: COLORS.textDark, border: 'none', background: 'none', outline: 'none', width: '100%', minWidth: 0, padding: 0 },
  hint:    { fontSize: 12, color: COLORS.textLight, margin: '6px 4px 0' },
  error:   { background: COLORS.dangerBg, color: COLORS.dangerText, padding: '8px 12px', borderRadius: 10, fontSize: 13, margin: '8px 0 0' },
  message: { width: '100%', boxSizing: 'border-box', borderRadius: 12, border: `1px solid ${COLORS.border}`, padding: '10px 12px', fontSize: 14, fontFamily: 'inherit', resize: 'none', margin: '10px 0 0', background: COLORS.white, color: COLORS.textDark },
  primaryBtn: { display: 'block', width: '100%', marginTop: 12, padding: '13px 8px', borderRadius: 26, border: 'none', background: COLORS.primary, color: COLORS.onPrimary, fontWeight: 700, fontSize: 15, cursor: 'pointer' },
  outlineBtn: { flex: 1, padding: '12px 8px', borderRadius: 26, border: `2px solid ${COLORS.primary}`, background: COLORS.white, color: COLORS.textDark, fontWeight: 700, fontSize: 14, cursor: 'pointer' },
  linkBtn: { display: 'block', margin: '8px auto 0', padding: '4px 8px', background: 'none', border: 'none', color: COLORS.textMid, fontSize: 13, fontWeight: 600, textDecoration: 'underline', cursor: 'pointer' },
  bidLine: { display: 'flex', alignItems: 'flex-start', gap: 8, background: COLORS.successBg, border: `1px solid ${COLORS.successBorder}`, color: COLORS.successText, borderRadius: 12, padding: '10px 12px', fontSize: 14, marginTop: 10 },
  bidSub:  { display: 'block', fontSize: 12, fontWeight: 400, marginTop: 2 },
  bidActions: { display: 'flex', alignItems: 'center', gap: 10, marginTop: 10 },
};
